"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { X, Plus, Scale, HelpCircle, ChevronDown, ChevronRight, Trash2 } from "lucide-react"

interface Question {
  id?: string
  questionText: string
  orderIndex: number
  riskLevel?: string
  parentQuestionId?: string | null
  subQuestions?: Question[]
}

interface CaseCategory {
  id: number
  icon?: string
  title: string
  ipcSections: string[]
  bnsSections: string[]
  questions?: Question[]
  createdAt: string
  updatedAt: string
  parentId?: number | null
}

interface CaseCategoryFormPopupProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (categoryData: Omit<CaseCategory, "id" | "createdAt" | "updatedAt">) => void
  initialData: CaseCategory | null
  title: string
}

export function CaseCategoryFormPopup({ isOpen, onClose, onSubmit, initialData, title }: CaseCategoryFormPopupProps) {
  const [formData, setFormData] = useState({
    icon: "",
    title: "",
    parentId: null as number | null,
    ipcSections: [] as string[],
    bnsSections: [] as string[],
    questions: [] as Question[],
  })

  const [newIpcSection, setNewIpcSection] = useState("")
  const [newBnsSection, setNewBnsSection] = useState("")
  const [newQuestion, setNewQuestion] = useState({
    questionText: "",
    riskLevel: "",
    parentQuestionId: null as string | null,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (initialData) {
      setFormData({
        icon: initialData.icon || "",
        title: initialData.title,
        parentId: initialData.parentId || null,
        ipcSections: [...initialData.ipcSections],
        bnsSections: [...initialData.bnsSections],
        questions: initialData.questions ? [...initialData.questions] : [],
      })
    } else {
      setFormData({
        icon: "",
        title: "",
        parentId: null,
        ipcSections: [],
        bnsSections: [],
        questions: [],
      })
    }
    setNewIpcSection("")
    setNewBnsSection("")
    setNewQuestion({
      questionText: "",
      riskLevel: "",
      parentQuestionId: null,
    })
    setErrors({})
    setExpandedQuestions(new Set())
  }, [initialData, isOpen])

  const handleInputChange = (field: string, value: string | number | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
  }

  const addSection = (type: "ipcSections" | "bnsSections", value: string) => {
    if (value.trim() && !formData[type].includes(value.trim())) {
      setFormData((prev) => ({
        ...prev,
        [type]: [...prev[type], value.trim()],
      }))
      if (type === "ipcSections") setNewIpcSection("")
      else setNewBnsSection("")
    }
  }

  const removeSection = (type: "ipcSections" | "bnsSections", index: number) => {
    setFormData((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }))
  }

  const addQuestion = () => {
    if (!newQuestion.questionText.trim()) return

    const question: Question = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      questionText: newQuestion.questionText.trim(),
      orderIndex: formData.questions.length,
      riskLevel: newQuestion.riskLevel || undefined,
      parentQuestionId: newQuestion.parentQuestionId || null,
      subQuestions: [],
    }

    setFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, question],
    }))

    setNewQuestion({
      questionText: "",
      riskLevel: "",
      parentQuestionId: null,
    })
  }

  const removeQuestion = (questionId: string) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId),
    }))
  }

  const updateQuestion = (questionId: string, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map(q =>
        q.id === questionId ? { ...q, [field]: value } : q
      ),
    }))
  }

  const getParentQuestions = () => {
    return formData.questions.filter(q => !q.parentQuestionId)
  }

  const getSubQuestions = (parentId: string) => {
    return formData.questions.filter(q => q.parentQuestionId === parentId)
  }

  const toggleQuestionExpansion = (questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(questionId)) {
        newSet.delete(questionId)
      } else {
        newSet.add(questionId)
      }
      return newSet
    })
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = "Category title is required"
    }
    if (formData.ipcSections.length === 0) {
      newErrors.ipcSections = "At least one IPC section is required"
    }
    if (formData.bnsSections.length === 0) {
      newErrors.bnsSections = "At least one BNS section is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter") {
      e.preventDefault()
      action()
    }
  }

  const renderQuestion = (question: Question, level: number = 0) => {
    const subQuestions = getSubQuestions(question.id!)
    const hasSubQuestions = subQuestions.length > 0
    const isExpanded = expandedQuestions.has(question.id!)

    return (
      <div key={question.id} className={`border border-gray-200 rounded-lg p-3 ${level > 0 ? 'ml-6 mt-2 bg-gray-50' : 'bg-white'}`}>
        <div className="flex items-start space-x-3">
          {hasSubQuestions && (
            <button
              type="button"
              onClick={() => toggleQuestionExpansion(question.id!)}
              className="mt-1 text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          )}
          <div className="flex-1 space-y-2">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600">Q{question.orderIndex + 1}:</span>
              {question.riskLevel && (
                <Badge variant={question.riskLevel === 'high' ? 'destructive' : question.riskLevel === 'medium' ? 'default' : 'secondary'} className="text-xs">
                  {question.riskLevel}
                </Badge>
              )}
            </div>
            <Textarea
              value={question.questionText}
              onChange={(e) => updateQuestion(question.id!, 'questionText', e.target.value)}
              className="border-gray-300 text-sm resize-none"
              rows={2}
              placeholder="Enter question text"
            />
            <div className="flex items-center space-x-2">
              <Select
                value={question.riskLevel || ""}
                onValueChange={(value) => updateQuestion(question.id!, 'riskLevel', value)}
              >
                <SelectTrigger className="w-32 h-8 text-xs border-gray-300">
                  <SelectValue placeholder="Risk Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => removeQuestion(question.id!)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        
        {hasSubQuestions && isExpanded && (
          <div className="mt-3">
            {subQuestions.map(subQ => renderQuestion(subQ, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[80vw] max-w-none h-[85vh] bg-white flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-black">{title}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 min-h-0">
          <div className="space-y-4 pb-4">
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-black">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">
                      Category Title <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange("title", e.target.value)}
                      className={`border-gray-300 ${errors.title ? "border-red-500" : ""}`}
                      placeholder="Enter category title in Hindi"
                    />
                    {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">Icon (Emoji)</Label>
                    <Input
                      value={formData.icon}
                      onChange={(e) => handleInputChange("icon", e.target.value)}
                      className="border-gray-300"
                      placeholder="🚨 (Optional emoji icon)"
                      maxLength={2}
                    />
                    <p className="text-xs text-gray-500">Use an emoji to represent this category</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-black flex items-center space-x-2">
                  <Scale className="h-5 w-5" />
                  <span>IPC Sections</span>
                  <span className="text-red-500">*</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex space-x-2">
                  <Input
                    value={newIpcSection}
                    onChange={(e) => setNewIpcSection(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, () => addSection("ipcSections", newIpcSection))}
                    className="border-gray-300 flex-1"
                    placeholder="Enter IPC section number"
                  />
                  <Button type="button" onClick={() => addSection("ipcSections", newIpcSection)} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.ipcSections.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.ipcSections.map((section, index) => (
                      <Badge key={index} variant="outline" className="border-gray-300">
                        Section {section}
                        <button
                          type="button"
                          onClick={() => removeSection("ipcSections", index)}
                          className="ml-2 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {errors.ipcSections && <p className="text-red-500 text-xs">{errors.ipcSections}</p>}
              </CardContent>
            </Card>

            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-black flex items-center space-x-2">
                  <Scale className="h-5 w-5" />
                  <span>BNS Sections</span>
                  <span className="text-red-500">*</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex space-x-2">
                  <Input
                    value={newBnsSection}
                    onChange={(e) => setNewBnsSection(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, () => addSection("bnsSections", newBnsSection))}
                    className="border-gray-300 flex-1"
                    placeholder="Enter BNS section number"
                  />
                  <Button type="button" onClick={() => addSection("bnsSections", newBnsSection)} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.bnsSections.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.bnsSections.map((section, index) => (
                      <Badge key={index} variant="outline" className="border-gray-300">
                        Section {section}
                        <button
                          type="button"
                          onClick={() => removeSection("bnsSections", index)}
                          className="ml-2 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {errors.bnsSections && <p className="text-red-500 text-xs">{errors.bnsSections}</p>}
              </CardContent>
            </Card>

            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-black flex items-center space-x-2">
                  <HelpCircle className="h-5 w-5" />
                  <span>Questions</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    <Textarea
                      value={newQuestion.questionText}
                      onChange={(e) => setNewQuestion(prev => ({ ...prev, questionText: e.target.value }))}
                      className="border-gray-300 resize-none"
                      rows={3}
                      placeholder="Enter question text (in Hindi)"
                    />
                    <div className="flex items-center space-x-3">
                      <Select
                        value={newQuestion.riskLevel}
                        onValueChange={(value) => setNewQuestion(prev => ({ ...prev, riskLevel: value }))}
                      >
                        <SelectTrigger className="w-40 border-gray-300">
                          <SelectValue placeholder="Risk Level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low Risk</SelectItem>
                          <SelectItem value="medium">Medium Risk</SelectItem>
                          <SelectItem value="high">High Risk</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={newQuestion.parentQuestionId || ""}
                        onValueChange={(value) => setNewQuestion(prev => ({ ...prev, parentQuestionId: value || null }))}
                      >
                        <SelectTrigger className="flex-1 border-gray-300">
                          <SelectValue placeholder="Parent Question (Optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no-parent">No Parent (Main Question)</SelectItem>
                          {getParentQuestions().map(q => (
                            <SelectItem key={q.id} value={q.id!}>
                              Q{q.orderIndex + 1}: {q.questionText.substring(0, 50)}...
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        type="button" 
                        onClick={addQuestion}
                        className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Question
                      </Button>
                    </div>
                  </div>
                </div>

                {formData.questions.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-black">Questions ({formData.questions.length})</h4>
                    <div className="space-y-3">
                      {getParentQuestions().map(question => renderQuestion(question))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        <div className="flex-shrink-0 flex justify-end space-x-2 pt-4 border-t border-gray-200 bg-white">
          <Button variant="outline" onClick={onClose} className="border-gray-300 bg-transparent">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            {initialData ? "Update Category" : "Create Category"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}